import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { StoreResponse, StoreResponseError, StoreResponseSuccess } from '@p2p/shared/domain';
import { map, Observable, take } from 'rxjs';

import { User } from '../entities';
import { CreateUserResponse } from '../entities/create-user-response';
import { UpsertUserData } from '../entities/upsert-user';
import { UsersDataService } from '../infrastructure/users-data.service';

import * as UsersActions from './users.actions';
import { UsersPageParams } from './users.model';
import * as UsersSelectors from './users.selectors';

@Injectable()
export class UsersFacade {
    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);
    private readonly usersDataService = inject(UsersDataService);

    usersList$ = this.store.select(UsersSelectors.selectUsersList);
    usersMap$ = this.store.select(UsersSelectors.selectUsersMap);
    usersIds$ = this.store.select(UsersSelectors.selectUsersIds);
    pageParams$ = this.store.select(UsersSelectors.selectPageParams);
    totalElements$ = this.store.select(UsersSelectors.selectTotalElements);
    loading$ = this.store.select(UsersSelectors.selectLoading);
    loaded$ = this.store.select(UsersSelectors.selectLoaded);

    loadUsers(pageParams: UsersPageParams = {}): void {
        this.store.dispatch(UsersActions.loadUsers({ pageParams }));
    }

    resetUsers(): void {
        this.store.dispatch(UsersActions.resetUsers());
    }

    setPageParams(pageParams: UsersPageParams): void {
        this.store.dispatch(UsersActions.setPageParams({ pageParams }));
    }

    createUser(user: UpsertUserData): void {
        this.store.dispatch(UsersActions.createUser({ user }));
    }

    editUser(user: UpsertUserData): void {
        this.store.dispatch(UsersActions.editUser({ user }));
    }

    get upsertUserResponse$(): Observable<StoreResponse<CreateUserResponse | unknown>> {
        return this.actions$.pipe(
            ofType(
                UsersActions.createUserSuccess,
                UsersActions.createUserFailure,
                UsersActions.editUserSuccess,
                UsersActions.editUserFailure
            ),
            map((action) => {
                let result: StoreResponse<CreateUserResponse | unknown> = { status: 'error' };

                if (action.type === UsersActions.createUserSuccess.type) {
                    result = new StoreResponseSuccess<CreateUserResponse>({
                        registrationToken: action.registrationToken
                    });
                }

                if (action.type === UsersActions.editUserSuccess.type) {
                    result = new StoreResponseSuccess<unknown>();
                }

                if (
                    action.type === UsersActions.createUserFailure.type ||
                    action.type === UsersActions.editUserFailure.type
                ) {
                    result = new StoreResponseError(action.error);
                }

                return result;
            }),
            take(1)
        );
    }

    deleteUsers(ids: number[]): void {
        this.store.dispatch(UsersActions.deleteUsers({ ids }));
    }

    get deleteUserResponse$(): Observable<StoreResponse<unknown>> {
        return this.actions$.pipe(
            ofType(UsersActions.deleteUsersSuccess, UsersActions.deleteUsersFailure),
            map((action) => {
                let result: StoreResponse<unknown> = { status: 'error' };

                if (action.type === UsersActions.deleteUsersSuccess.type) {
                    result = new StoreResponseSuccess<unknown>();
                }

                if (action.type === UsersActions.deleteUsersFailure.type) {
                    result = new StoreResponseError(action.error);
                }

                return result;
            }),
            take(1)
        );
    }

    changeUserBlocked(id: User['id'], blocked: boolean): void {
        this.store.dispatch(UsersActions.changeUserBlocked({ id, blocked }));
    }

    get changeUserBlockedResponse$(): Observable<StoreResponse<unknown>> {
        return this.actions$.pipe(
            ofType(UsersActions.changeUserBlockedSuccess, UsersActions.changeUserBlockedFailure),
            map((action) => {
                let result: StoreResponse<unknown> = { status: 'error' };

                if (action.type === UsersActions.changeUserBlockedSuccess.type) {
                    result = new StoreResponseSuccess<unknown>();
                }

                if (action.type === UsersActions.changeUserBlockedFailure.type) {
                    result = new StoreResponseError(action.error);
                }

                return result;
            }),
            take(1)
        );
    }
}
